import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';

/**
 * The caller's own in-app notifications -- `/api/v1/notifications/in-app`.
 *
 * Scoped to the signed-in user by the server and by nothing else: a notification belongs to its recipient,
 * so there is no permission to send and no id to pass. See `InAppNotificationService` and
 * `openspec/notification/spec.md` in the platform repository.
 */

/** How loudly the row is drawn. Tone, not category: {@link AppNotification.kind} says what it is about. */
export type NotificationSeverity = 'ACTION' | 'INFO' | 'SUCCESS' | 'WARNING' | 'DANGER';

/** The panel's three tabs, spelt as the server names them. */
export type NotificationTab = 'ALL' | 'UNREAD' | 'ACTION_REQUIRED';

export interface AppNotification {
  id: number;
  /** What sort of thing it is, which the panel picks the row's icon from: MASTER_IMPORT, BID_APPROVAL, … */
  kind: string;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  /** The record's reference as a person quotes it (`BID-2026-0051`), drawn as a chip. */
  reference: string | null;
  /** The words on the row's button. Null for a notification that is only worth reading. */
  actionLabel: string | null;
  /**
   * Whether it belongs in the Action Required tab.
   *
   * Sent by the server even though it is `actionLabel !== null`, and read from the response rather than
   * re-derived here: re-deriving it is how the tab and its count come to disagree.
   */
  actionRequired: boolean;
  /** Where it points within the application, or null for one that is only worth reading. */
  link: string | null;
  read: boolean;
  createdAt: string;
}

/** The bell's badge and the three tab counts, counted together by the server. */
export interface NotificationCounts {
  all: number;
  unread: number;
  actionRequired: number;
}

/** One page of one tab. `totalElements` is that tab's own count. */
export interface NotificationPage {
  content: AppNotification[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

const FALLBACK_ERROR = 'Notifications could not be loaded.';

/** What the panel asks for when it opens: enough to fill the drawer without paging. */
export const NOTIFICATION_PAGE_SIZE = 20;

/**
 * One tab's notifications, newest first.
 *
 * The order is the server's and is not negotiable -- there is no `sort` parameter -- so a client cannot
 * ask for the oldest by accident.
 */
export async function fetchNotifications(
  tab: NotificationTab = 'ALL',
  page = 0,
  size: number = NOTIFICATION_PAGE_SIZE,
): Promise<NotificationPage> {
  return unwrapEnvelope(
    await apiClient.get<ApiEnvelope<NotificationPage>>('/notifications/in-app', {
      params: { tab, page, size },
    }),
    FALLBACK_ERROR,
  );
}

/**
 * The badge and both tab counts in one call.
 *
 * Deliberately not three calls, and deliberately not `/unread-count` plus arithmetic over a page: the
 * badge, the subtitle and the numbers beside the tab labels are read at one glance, and counting them at
 * one moment is what stops the badge saying 4 while the tab beside it says 3. `unread` and
 * `actionRequired` are each a subset of `all` and overlap each other, so they are not meant to add up.
 */
export async function fetchNotificationCounts(): Promise<NotificationCounts> {
  return unwrapEnvelope(
    await apiClient.get<ApiEnvelope<NotificationCounts>>('/notifications/in-app/counts'),
    FALLBACK_ERROR,
  );
}

/**
 * Marks one notification read, and answers with the row.
 *
 * The click is read-then-navigate, so the caller follows the `link` on THIS response rather than one it
 * cached when the panel opened. Marking an already-read notification read again is not an error.
 */
export async function markNotificationRead(id: number): Promise<AppNotification> {
  return unwrapEnvelope(
    await apiClient.post<ApiEnvelope<AppNotification>>(`/notifications/in-app/${String(id)}/read`),
    FALLBACK_ERROR,
  );
}

/**
 * Marks every one of the caller's notifications read, and answers with how many were still unread.
 *
 * It empties the badge. It does **not** empty the Action Required tab: opening an approval request does
 * not approve it.
 */
export async function markAllNotificationsRead(): Promise<number> {
  return unwrapEnvelope(
    await apiClient.post<ApiEnvelope<number>>('/notifications/in-app/read-all'),
    FALLBACK_ERROR,
  );
}
